// Avatar and AvatarStack: initials in a face, and a row of them overlapping for a team.
// A person may choose their face's colour, shape and one accessory (docs/features/user-avatars.md);
// with nothing chosen it is the default circle in the surface tone.
// docs/operations/design-standard.md, "Shape" (avatars are one of the pill's few uses).

import React from 'react';
import useUserStore from '../../stores/useUserStore';
import { ACCESSORY_HEADROOM, SHAPES, accessoryArt, isAvatarAccessory, isAvatarShape } from './avatarShapes';
import type { AccessoryFill, AccessoryPart, AvatarAccessory, AvatarShape } from './avatarShapes';

export const initials = (name: string | null | undefined): string => {
  const parts = (name || '').trim().split(/\s+/).filter(Boolean);
  if (parts.length === 0) return '?';
  return ((parts[0][0] || '') + (parts.length > 1 ? parts[parts.length - 1][0] : '')).toUpperCase();
};

const HEX = /^#[0-9a-f]{6}$/i;
export const isAvatarColor = (v: unknown): v is string => typeof v === 'string' && HEX.test(v);

const channels = (hex: string) => [1, 3, 5].map((i) => parseInt(hex.slice(i, i + 2), 16));

/** WCAG relative luminance of a #rrggbb colour. */
export function luminance(hex: string): number {
  const [r, g, b] = channels(hex).map((c) => {
    const s = c / 255;
    return s <= 0.03928 ? s / 12.92 : ((s + 0.055) / 1.055) ** 2.4;
  });
  return 0.2126 * r + 0.7152 * g + 0.0722 * b;
}

export const contrast = (a: string, b: string): number => {
  const [hi, lo] = [luminance(a), luminance(b)].sort((x, y) => y - x);
  return (hi + 0.05) / (lo + 0.05);
};

/**
 * The initials' ink on a chosen fill: white (on-surface) or black (on-primary), whichever
 * contrasts more. One of the two always clears 4.5:1 against any opaque colour.
 */
export const inkFor = (fill: string): 'light' | 'dark' => (contrast(fill, '#ffffff') >= contrast(fill, '#000000') ? 'light' : 'dark');
const INK = { light: 'var(--on-surface)', dark: 'var(--on-primary)' };

// The track tone (--surface-container-high): a draft face recedes halfway toward it.
const TRACK = '#323232';
const recede = (hex: string): string => {
  const a = channels(hex);
  const b = channels(TRACK);
  return `#${a.map((c, i) => Math.round((c + b[i]) / 2).toString(16).padStart(2, '0')).join('')}`;
};

export type AvatarTone = 'solid' | 'draft';

export interface AvatarProps {
  name: string | null | undefined;
  /** `draft` recedes a step, for a slot not yet confirmed. */
  tone?: AvatarTone;
  size?: 'sm' | 'md';
  /** The ring separating overlapped faces; match it to the surface they sit on. */
  ring?: string;
  /** Inside a labelled group the face is not announced on its own. */
  decorative?: boolean;
  /** The person's chosen fill, `#rrggbb`. Absent or null: the surface tone. */
  color?: string | null;
  /** The person's chosen shape. Absent or null: the circle. */
  shape?: AvatarShape | string | null;
  /** The person's chosen accessory, drawn around the face and never over the initials. */
  accessory?: AvatarAccessory | string | null;
  /** Whose face this is. The signed-in person's own face follows their latest choice without a refetch. */
  userId?: string | null;
  className?: string;
}

const AV_SIZE = { sm: 'h-6 w-6 text-[10px]', md: 'h-8 w-8 text-xs' };
const AV_PX = { sm: 24, md: 32 };

/** What each accessory part is painted with. */
const ACCESSORY_FILL: Record<AccessoryFill, string> = {
  accent: 'var(--color-primary, #ffcc33)',
  light: 'var(--on-surface-variant)',
  mid: 'color-mix(in srgb, var(--on-surface-variant) 55%, var(--surface))',
  white: 'var(--on-surface)',
  face: 'var(--av-fill)',
  faceInner: 'color-mix(in srgb, var(--av-fill) 55%, var(--av-ink))',
};

const LAYER = 'pointer-events-none absolute inset-0 h-full w-full overflow-visible';

/** Front parts carry the ring, like the face, so they read over a neighbour; back parts sit behind the face and need none. */
const Layer: React.FC<{ parts: AccessoryPart[]; outlined: boolean }> = ({ parts, outlined }) =>
  parts.length ? (
    <svg aria-hidden="true" viewBox="0 0 100 100" fill="none" className={LAYER}>
      {parts.map((p, i) => (
        <path
          key={i}
          d={p.d}
          fillRule="evenodd"
          style={{ fill: ACCESSORY_FILL[p.fill], stroke: outlined ? 'var(--av-ring)' : 'none' }}
          strokeWidth={3}
          strokeLinejoin="round"
          vectorEffect="non-scaling-stroke"
          paintOrder="stroke"
          className={p.glow ? 'drop-shadow-glow' : undefined}
        />
      ))}
    </svg>
  ) : null;

type Me = { id?: string; avatar_color?: string | null; avatar_shape?: string | null; avatar_accessory?: string | null };

export const Avatar: React.FC<AvatarProps> = ({
  name, tone = 'solid', size = 'sm', ring = 'ring-surface-container-low', decorative, color, shape, accessory, userId, className = '',
}) => {
  const me = useUserStore((s: { user: Me }) => s.user);
  // The store learns its own avatar from Settings; until then the payload's copy stands.
  const mine = !!userId && me.id === userId && me.avatar_color !== undefined;
  const chosenColor = mine ? me.avatar_color : color;
  const chosenShape = mine ? me.avatar_shape : shape;
  const chosenAccessory = mine ? me.avatar_accessory : accessory;

  const fill = isAvatarColor(chosenColor) ? (tone === 'draft' ? recede(chosenColor) : chosenColor) : null;
  const spec = SHAPES[isAvatarShape(chosenShape) ? chosenShape : 'circle'];
  const extra = isAvatarAccessory(chosenAccessory) ? chosenAccessory : null;
  const a11y = {
    role: decorative ? undefined : 'img',
    'aria-label': decorative ? undefined : name || 'Someone',
    'aria-hidden': decorative || undefined,
  } as const;
  const toneText = tone === 'solid' ? 'text-on-surface' : 'text-on-surface-variant';

  // Nothing chosen: today's face exactly. Every face is `relative`, drawn or not, so
  // overlapped faces in a stack paint in DOM order; a positioned face among unpositioned
  // ones would jump above its neighbours.
  if (!fill && spec.key === 'circle' && !extra) {
    return (
      <span
        {...a11y}
        className={`relative inline-flex flex-none select-none items-center justify-center rounded-full font-bold ring-2 transition-[background-color,color] duration-quick ease-emphasized ${ring} ${AV_SIZE[size]} ${
          tone === 'solid' ? 'bg-surface-container-highest text-on-surface' : 'bg-surface-container-high text-on-surface-variant'
        } ${className}`}
      >
        {initials(name)}
      </span>
    );
  }

  // Drawn faces: the ring is the path's own stroke, painted under the fill, so it follows
  // the outline the way ring-2 follows a circle. The ring class still sets --tw-ring-color
  // on this span. Tailwind's preflight resets that variable on EVERY element, the path
  // included, so it is resolved here into --av-ring, which inherits.
  const toneFill = tone === 'solid' ? 'var(--surface-container-highest)' : 'var(--surface-container-high)';
  const ink = fill ? INK[inkFor(fill)] : tone === 'solid' ? 'var(--on-surface)' : 'var(--on-surface-variant)';
  const art = extra ? accessoryArt(spec.key, extra) : null;
  const vars = { '--av-ring': 'var(--tw-ring-color)', '--av-fill': fill || toneFill, '--av-ink': ink } as React.CSSProperties;
  return (
    <span
      {...a11y}
      style={{ ...vars, ...(fill ? { color: ink } : null) }}
      className={`relative inline-flex flex-none select-none items-center justify-center font-bold ${ring} ${AV_SIZE[size]} ${fill ? '' : toneText} ${className}`}
    >
      {art && <Layer parts={art.back} outlined={false} />}
      <svg aria-hidden="true" viewBox="0 0 100 100" fill="none" className={LAYER}>
        <path
          d={spec.d}
          style={{ fill: 'var(--av-fill)', stroke: 'var(--av-ring)' }}
          strokeWidth={4}
          strokeLinejoin="round"
          vectorEffect="non-scaling-stroke"
          paintOrder="stroke"
          className="transition-[fill] duration-quick ease-emphasized"
        />
      </svg>
      {art && <Layer parts={art.front} outlined />}
      <span className="relative" style={spec.dy ? { top: `${spec.dy}%` } : undefined}>{initials(name)}</span>
    </span>
  );
};

export interface AvatarPerson {
  key?: string;
  name: string | null | undefined;
  tone?: AvatarTone;
  note?: string;
  color?: string | null;
  shape?: string | null;
  accessory?: string | null;
  userId?: string | null;
}

export interface AvatarStackProps {
  people: AvatarPerson[];
  /** Faces drawn before the rest fold into a +n. */
  max?: number;
  size?: 'sm' | 'md';
  ring?: string;
  /** The spoken summary; defaults to every name, with each person's note. */
  label?: string;
  /** What shows when there is nobody. */
  empty?: React.ReactNode;
  className?: string;
}

export const AvatarStack: React.FC<AvatarStackProps> = ({ people, max = 4, size = 'sm', ring, label, empty = null, className = '' }) => {
  if (people.length === 0) return <>{empty}</>;
  const shown = people.slice(0, max);
  const rest = people.length - shown.length;
  const spoken = label || people.map((p) => `${p.name || 'Someone'}${p.note ? `, ${p.note}` : ''}`).join('; ');
  // A hat or a halo rises above the face; the row makes room rather than clipping it.
  const headroom = shown.some((p) => isAvatarAccessory(p.accessory)) ? { paddingTop: `${(AV_PX[size] * ACCESSORY_HEADROOM) / 100}px` } : undefined;
  return (
    <span role="img" aria-label={spoken} style={headroom} className={`inline-flex items-center ${className}`}>
      {shown.map((p, i) => (
        <Avatar
          key={p.key || `${p.name}-${i}`}
          name={p.name}
          tone={p.tone}
          size={size}
          ring={ring}
          color={p.color}
          shape={p.shape}
          accessory={p.accessory}
          userId={p.userId}
          decorative
          className={i > 0 ? '-ml-1' : ''}
        />
      ))}
      {rest > 0 && (
        <span
          aria-hidden="true"
          className={`relative -ml-1 inline-flex flex-none items-center justify-center rounded-full bg-surface-container-high font-semibold tabular-nums text-on-surface-variant ring-2 ${ring || 'ring-surface-container-low'} ${AV_SIZE[size]}`}
        >
          +{rest}
        </span>
      )}
    </span>
  );
};

export default Avatar;
